export const API_BASE = process.env.REACT_APP_API_URL || '/api';
const IDENTITY_URL = 'https://identity.intechweben.com/api/v1';
export const RELM = 'mydocumentsign.com';

export function getCookie(name) {
  const match = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith(name + '='));
  return match ? match.split('=')[1] : null;
}

export function setCookie(name, value, expires) {
  let cookie = `${name}=${value}; path=/`;
  if (expires) cookie += `; expires=${expires.toUTCString()}`;
  document.cookie = cookie;
}

export function clearSession() {
  ['jwtToken', 'renewalToken', 'displayName'].forEach(name => {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  });
}

export function parseJwtPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch { return null; }
}

function getCookieExpiry() {
  return new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
}

export function isTokenValid() {
  const token = getCookie('jwtToken');
  if (!token) return false;
  const payload = parseJwtPayload(token);
  if (!payload || !payload.exp) return false;
  return payload.exp * 1000 > Date.now() + 5000;
}

function shouldRenewSoon() {
  const token = getCookie('jwtToken');
  if (!token) return false;
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return false;
  const remaining = payload.exp * 1000 - Date.now();
  return remaining > 5000 && remaining < 120000;
}

let renewalPromise = null;

async function renewToken() {
  const renewal = getCookie('renewalToken');
  if (!renewal) return false;
  try {
    const res = await fetch(`${IDENTITY_URL}/token/renew`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ renewalToken: renewal }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    const expires = getCookieExpiry();
    setCookie('jwtToken', data.jwtToken, expires);
    setCookie('renewalToken', data.renewalToken, expires);
    return true;
  } catch {
    return false;
  }
}

export async function ensureValidToken() {
  if (isTokenValid()) return true;
  if (!renewalPromise) renewalPromise = renewToken().finally(() => { renewalPromise = null; });
  const renewed = await renewalPromise;
  if (!renewed) { clearSession(); return false; }
  return true;
}

const OFFLINE_RESPONSE = { ok: false, status: 0, json: () => Promise.resolve([]), text: () => Promise.resolve('') };

export async function api(path, options = {}) {
  const valid = await ensureValidToken();
  if (!valid) { window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`; return OFFLINE_RESPONSE; }
  const jwtToken = getCookie('jwtToken');
  const headers = { ...options.headers };
  if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
  if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  if (shouldRenewSoon() && !renewalPromise) {
    renewalPromise = renewToken().finally(() => { renewalPromise = null; });
  }
  return fetch(`${API_BASE}${path}`, { ...options, headers }).then(res => {
    if (res.status === 401) {
      clearSession();
      window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
    }
    return res;
  }).catch(() => OFFLINE_RESPONSE);
}

// Public API call (no auth needed, for signing pages)
export async function publicApi(path, options = {}) {
  const headers = { ...options.headers };
  if (options.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  return fetch(`${API_BASE}${path}`, { ...options, headers }).catch(() => OFFLINE_RESPONSE);
}
